import PropTypes from 'prop-types'
import { useFormContext } from 'react-hook-form'
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  FormGroupContainer,
  Typography,
  FormTextStyled,
  DropZoneLabelRow,
  RequiredMark,
  DropZoneArea,
  DropZoneFileInput,
  DropZoneIconCircle,
} from '../styled'
import theme from '../../constants/theme'
import FileText from '../../assets/svgs/FileText'
import UploadArrow from '../../assets/svgs/UploadArrow'

export default function FileDropInput({
  name,
  label,
  placeholder: _placeholder,
  helperText,
  required,
  ...rest
}) {
  const {
    register,
    formState: { errors },
    setValue,
  } = useFormContext()
  const { t } = useTranslation()
  const [fileName, setFileName] = useState('')
  const [isDragging, setIsDragging] = useState(false)
  const disabled = rest.disabled
  const isError = !!errors[name]

  useEffect(() => {
    register(name)
  }, [name, register])

  const applyFiles = (files) => {
    if (files && files.length > 0) {
      setFileName(files[0].name)
      setValue(name, files, { shouldValidate: true, shouldDirty: true })
    } else {
      setFileName('')
      setValue(name, null, { shouldValidate: true, shouldDirty: true })
    }
  }

  const handleFileChange = (e) => {
    applyFiles(e.target.files)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setIsDragging(false)
    if (disabled) return
    applyFiles(e.dataTransfer.files)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    if (!disabled) setIsDragging(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    setIsDragging(false)
  }

  return (
    <FormGroupContainer className="mb-3">
      <DropZoneLabelRow>
        <Typography size="13px" bold color={theme.talentinoTextGray}>
          {label}
          {required && <RequiredMark> *</RequiredMark>}
        </Typography>
        {rest.accept && (
          <Typography size="12px" color={theme.talentinoSecondaryGray}>
            {t('positionForm.documentUpload.onlyFormat', {
              format: rest.accept.toUpperCase(),
            })}
          </Typography>
        )}
      </DropZoneLabelRow>

      <DropZoneArea
        htmlFor={`filedrop-${name}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        isDragging={isDragging}
        isError={isError}
        isDisabled={disabled}
      >
        <DropZoneFileInput
          id={`filedrop-${name}`}
          type="file"
          onChange={handleFileChange}
          disabled={disabled}
          accept={rest.accept}
        />
        <DropZoneIconCircle>
          {fileName ? (
            <FileText width="20" height="20" color={theme.brandColorOrange} />
          ) : (
            <UploadArrow color={theme.brandColorOrange} />
          )}
        </DropZoneIconCircle>

        {fileName ? (
          <Typography size="14px" bold color={theme.talentinoBlack}>
            {fileName}
          </Typography>
        ) : (
          <>
            <Typography
              size="15px"
              bold
              color={theme.talentinoBlack}
              className="d-block mb-1"
            >
              {t('positionForm.documentUpload.dragDropFile')}
            </Typography>
            <Typography size="13px" color={theme.brandColorOrange}>
              {t('positionForm.documentUpload.orBrowseFiles')}
            </Typography>
          </>
        )}
      </DropZoneArea>

      {(isError || helperText) && (
        <FormTextStyled isError={isError}>
          {isError ? errors[name].message : helperText}
        </FormTextStyled>
      )}
    </FormGroupContainer>
  )
}

FileDropInput.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  helperText: PropTypes.string,
  required: PropTypes.bool,
}
